<template>
  <div class="kpi-averages-and-trends">
    <HorizontalLoading v-if="CategoryTrends.isLoading" />
    <div class="averages-list">
      <div class="header">
        <div class="title">
          <span> {{ normalizeSelectedKpi }} </span>
          <div class="sub-title">
            {{ $t('Category Score Averages') }}

            <div class="mobile-arrow">
              <i v-if="toggleArrow === false"
                 class="mdi mdi-chevron-right"
                 @click="toggleArrow = !toggleArrow" />
              <i v-if="toggleArrow === true"
                 class="mdi mdi-chevron-up"
                 @click="toggleArrow = !toggleArrow" />
            </div>
          </div>
        </div>
        <div v-show="toggleArrow === true" class="legend-buttons">
          <div class="grayButton"
               :class="{ active: !ExecutiveViewKpiBus.allClear }"
               @click="ExecutiveViewKpiBus.selectStatus('clear')">
            {{ $t('None') }}
          </div>
          <div class="grayButton"
               :class="{ active: !ExecutiveViewKpiBus.allSelect }"
               @click="ExecutiveViewKpiBus.selectStatus('all')">
            {{ $t('All') }}
          </div>
        </div>
      </div>
      <div class="legend-list">
        <template v-for="(item, index) in series">
          <div v-show="toggleArrow === true"
               v-if="item.legends[index]"
               :key="index"
               class="item kpi-table"
               :class="{ active: !item.legends[index].visible }"
               @click="ExecutiveViewKpiBus.toggleLegend(item.legends[index].name)">
            <template>
              <div class="score-name">
                {{ normalizeString(item.name) }}
              </div>
              <div v-if="item.data[0] !== null" class="score-value">
                {{ item.data[0].toFixed() }} {{ templateProps.symbol }}
              </div>
              <div class="round-colors" :style="{ backgroundColor: item.legends[index].color }" />
            </template>
          </div>
        </template>
      </div>
      <div class="hideArea">
        <div class="chart" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  chain,
  result,
  isEqual,
  sortBy
} from 'lodash';

import { createCrudState } from 'vuex-module-generator';
import Highcharts from 'highcharts';
import { mapState, mapGetters } from 'vuex';
import i18n from '@/i18n';
import { fetchKpiViewByCategory } from '../api';
import { getTemplateProps, formatDataLabel } from '../../executive-view/utils';
import ExecutiveViewKpiBus from '../bus';

export default {
  props: {
    selectedKpi: {
      type: String,
      required: true
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    periods: {
      type: Object,
      required: true
    },
    auditorAndStoreFilters: {
      type: Object,
      required: true
    },
    collectedTags: {
      type: Array,
      default() {
        return [];
      }
    },
    teamGroupFilter: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      ExecutiveViewKpiBus,
      CategoryTrends: createCrudState().list,
      toggleArrow: true,
      widthPage: 0
    };
  },
  computed: {
    ...mapState(['Project']),
    ...mapGetters(['scoreTemplates']),
    filteredScoreTemplates() {
      return this.scoreTemplates.filter(i => i.show_executive_view);
    },
    selectedScoreTemplate() {
      return this.filteredScoreTemplates.find(i => i.name === this.selectedKpi);
    },
    collectedTagsIds() {
      return this.collectedTags.reduce((acc, item) => {
        acc.push(...item.tags.map(tag => tag.id));

        return acc;
      }, []);
    },
    templateProps() {
      return getTemplateProps(this.selectedKpi);
    },
    series() {
      const checkVisibility = (item) => {
        const foundLegend = ExecutiveViewKpiBus.legends.find(l => l.name === item);

        return foundLegend ? foundLegend.visible : true;
      };

      const current = chain(this.CategoryTrends.data.current)
        .find(item => item.key === this.selectedKpi)
        .value();

      const previous = chain(this.CategoryTrends.data.previous)
        .find(item => item.key === this.selectedKpi)
        .value();

      let list = chain(current)
        .result('value', [])
        .map(item => ({
          name: item.key,
          data: [item.value],
          legends: ExecutiveViewKpiBus.legends,
          visible: checkVisibility(item.key),
          previous: result(previous, [this.selectedKpi, 'value', item.key])
        }))
        .value();

      if (this.selectedScoreTemplate.categories) {
        list = list.filter(i => this.selectedScoreTemplate.categories[i.name] !== false);
      }

      return sortBy(list, 'name');
    },
    normalizeSelectedKpi() {
      return this.selectedKpi ? i18n.t('{kpiName}', { kpiName: this.normalizeString(this.selectedKpi) }) : '';
    }
  },
  watch: {
    selectedKpi() {
      this.renderChart();
    },
    periods() {
      this.fetchData();
    },
    auditorAndStoreFilters() {
      this.fetchData();
    },
    collectedTagsIds(oldVal, newVal) {
      if (!isEqual(oldVal, newVal)) {
        this.fetchData();
      }
    },
    teamGroupFilter() {
      this.fetchData();
    }
  },
  created() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  mounted() {
    this.fetchData();

    ExecutiveViewKpiBus.$on('toggleLegend', () => {
      this.renderChart();
    });
  },
  methods: {
    renderChart() {
      const {
        templateProps, normalizeSelectedKpi, periods, auditorAndStoreFilters, Project
      } = this;

      const getOptions = () => ({
        exporting: {
          enabled: true,
          chartOptions: {
            legend: { enabled: true },
            title: { text: `${Project.active.data.display_name} - ${normalizeSelectedKpi}` },
            subtitle: {
              text: `${periods.selectedYear}
             - ${auditorAndStoreFilters.auditor_ids} ${auditorAndStoreFilters.store_ids}`
            },
            plotOptions: {
              column: {
                dataLabels: {
                  enabled: true,
                  useHTML: false,
                  formatter() {
                    return this.y.toFixed(2);
                  }
                }
              }
            }
          }
        },
        chart: {
          type: 'column'
        },
        xAxis: { labels: { enabled: false } },
        yAxis: { title: '' },
        title: { text: '' },
        legend: {
          enabled: false
        },
        credits: { enabled: false },
        plotOptions: {
          series: {
            groupPadding: 0
          },
          column: {
            dataLabels: {
              enabled: true,
              useHTML: true,
              align: 'center',
              formatter() {
                const { y } = this.point;
                const { previous } = this.series.userOptions;

                return formatDataLabel(y, previous, templateProps);
              }
            }
          }
        },
        tooltip: {
          headerFormat: '',
          pointFormatter() {
            const { float_digits, symbol } = templateProps;

            return this.tooltipFormatter(`<span style="color:{point.color}">●</span> {series.name}: <b>${this.y.toFixed(float_digits)}${symbol}</b><br/>`);
          }
        },
        series: this.series
      });

      if (this.chart) {
        this.chart.update(getOptions(), true, true, true);
      } else {
        this.chart = Highcharts.chart(this.$el.querySelector('.chart'), getOptions());
      }

      const legends = this.chart.series.map(serie => ({
        name: serie.name,
        visible: serie.visible,
        color: serie.color
      }));

      ExecutiveViewKpiBus.setLegends(legends);
    },
    async fetchData() {
      const {
        projectId, periods, auditorAndStoreFilters, teamGroupFilter
      } = this;

      this.CategoryTrends.isLoading = true;

      try {
        const { data: { data: current } } = await fetchKpiViewByCategory(projectId, {
          filter: {
            stores: {
              tags: this.collectedTagsIds
            },
            project: projectId,
            auditor_ids: auditorAndStoreFilters.auditor_ids,
            store_ids: auditorAndStoreFilters.store_ids,
            team_group_id: teamGroupFilter.id,
            start_date_with_time: this.convertToServerTimeZone(periods.current.start_time_without_utc),
            end_date_with_time: this.convertToServerTimeZone(periods.current.end_time_without_utc)
          }
        });

        const { data: { data: previous } } = await fetchKpiViewByCategory(projectId, {
          filter: {
            stores: {
              tags: this.collectedTagsIds
            },
            project: projectId,
            auditor_ids: auditorAndStoreFilters.auditor_ids,
            store_ids: auditorAndStoreFilters.store_ids,
            team_group_id: teamGroupFilter.id,
            ...((periods.previous && periods.previous.start_time) && { start_date_with_time: this.convertToServerTimeZone(periods.previous.start_time, undefined, true) }),
            ...((periods.previous && periods.previous.end_time) && { end_date_with_time: this.convertToServerTimeZone(periods.previous.end_time, undefined, true) })
          }
        });

        this.CategoryTrends.data = {
          current,
          previous
        };

        await this.$nextTick();

        this.renderChart();

        this.CategoryTrends.isLoaded = true;
      } catch (e) {
        this.CategoryTrends.hasError = true;
      }

      this.CategoryTrends.isLoading = false;
    },
    handleResize() {
      this.widthPage = window.innerWidth;
      if (window.innerWidth > 640) {
        this.toggleArrow = true;
      }
    }
  }
};
</script>

<style lang="scss">
.hideArea {
  display: none !important;
}

.kpi-averages-and-trends {
  position: relative;
  box-sizing: border-box;
  padding: 30px 5px;
  color: #474747;
  background: white;

  .el-row {
    width: 100%;
  }

  .trends {
    .trend-types {
      .el-button {
        font-size: 11px;
        border: 0;

        @include breakpoint(lg-max) {
          font-size: 14px;
          font-weight: 600;
        }
      }
    }

    .trend-chart {
      margin-top: 30px;
    }
  }

  @include breakpoint(lg-max) {
    padding: 0;
    border-radius: 6px;
  }
}

.legend-buttons {
  display: flex;
  justify-content: flex-start;
  height: 25px;

  @include breakpoint(lg-max) {
    justify-content: flex-end;
  }

  .grayButton {
    padding: 4px 13px 4px 13px;
    margin-left: 7px;
    font-size: 12px;
    color: #fff;
    cursor: pointer;
    background-color: #535353;
    border-radius: 13px;
    opacity: 0.3;

    &.active {
      opacity: 1;
    }
  }
}

.averages-list {
  .legend-list {
    max-height: 400px;
    padding-right: 10px;
    margin-top: 20px;
    overflow: scroll;

    @include breakpoint(lg-max) {
      padding-right: 0;
      margin-top: 0;
    }
  }

  .header {
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @include breakpoint(lg-max) {
      flex-direction: row;
      padding: 10px;
      border-bottom: 1px solid #f5f5f5;
    }

    @include breakpoint(md-s-max) {
      flex-direction: column;
    }

    .mobile-arrow {
      display: none;
    }

    .title {
      width: 100%;

      span {
        font-size: 18px;
        font-weight: 600;
        color: $darkSkyBlue;
      }

      .sub-title {
        font-size: 18px;
        font-weight: 400;
      }

      @include breakpoint(lg-max) {
        display: flex;
        margin-bottom: 0;

        span {
          margin-right: 14px;
        }

        .sub-title {
          display: flex;
          align-items: center;

          .mobile-arrow {
            i {
              font-size: 30px;
              color: $vispera-blue;
            }
          }
        }
      }

      @include breakpoint(md-s-max) {
        flex-direction: column;

        span {
          margin-right: unset;
          font-size: 16px;
        }

        .sub-title {
          font-size: 22px;
        }
      }
    }
  }
}

.kpi-table {
  cursor: pointer;

  .round-colors {
    width: 20px;
    height: 30px;
    margin-left: 5px;
    border-top-left-radius: 7px;
    border-bottom-left-radius: 7px;
  }

  &.active {
    opacity: 0.3;
  }
}

.categories-kpi-score-averages {
  position: relative;
  box-sizing: border-box;
  padding: 30px;
  color: #474747;
  background: white;

  .el-row {
    width: 100%;
  }

  .trends {
    .trend-types {
      .el-button {
        font-size: 11px;
        border: 0;
      }
    }

    .trend-chart {
      margin-top: 30px;
    }
  }
}

@include breakpoint(md-s-max) {
  .averages-list .header {
    .mobile-arrow {
      display: flex;
    }

    .legend-buttons {
      justify-content: flex-start;
    }

    .title {
      display: flex;
      justify-content: space-between;

      i {
        font-size: 30px;
      }
    }
  }
}
</style>
